import { useState, useEffect } from 'react';
import { PlatformLayout } from '../components/layout/PlatformLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';
import { RefreshCw, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return '-'; }
}

function SyncStatusBadge({ status }) {
  if (status === 'ok') return <Badge className="bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[11px]">OK</Badge>;
  if (status === 'error') return <Badge variant="destructive" className="text-[11px]">Erreur</Badge>;
  return <Badge variant="secondary" className="text-[11px]">Jamais</Badge>;
}

function TypeIcon({ type }) {
  if (type === 'email') return <Mail className="w-3.5 h-3.5 text-blue-500" />;
  return <Phone className="w-3.5 h-3.5 text-emerald-500" />;
}

export default function PlatformSourcesPage() {
  const { token } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/platform/sources`, { headers: { Authorization: `Bearer ${token}` } });
      setSources(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSources(); }, []);

  const emailCount = sources.filter(s => s.type === 'email').length;
  const ringoverCount = sources.filter(s => s.type === 'ringover').length;
  const errorCount = sources.filter(s => s.status === 'error').length;

  return (
    <PlatformLayout title="Sources de donnees">
      <div className="space-y-4" data-testid="platform-sources-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            <Badge variant="outline" className="gap-1.5 py-1"><Mail className="w-3 h-3" /> {emailCount} email(s)</Badge>
            <Badge variant="outline" className="gap-1.5 py-1"><Phone className="w-3 h-3" /> {ringoverCount} ringover</Badge>
            {errorCount > 0 && <Badge variant="destructive" className="gap-1.5 py-1">{errorCount} en erreur</Badge>}
          </div>
          <Button variant="outline" size="sm" onClick={fetchSources} disabled={loading} data-testid="refresh-sources">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Label</TableHead>
                  <TableHead className="text-xs">Tenant</TableHead>
                  <TableHead className="text-xs">Derniere synchro</TableHead>
                  <TableHead className="text-xs">Statut</TableHead>
                  <TableHead className="text-xs">Erreur</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">Chargement...</TableCell></TableRow>
                ) : sources.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">Aucune source configuree</TableCell></TableRow>
                ) : sources.map((s, i) => (
                  <TableRow key={s.id || i} data-testid={`source-row-${s.id || i}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TypeIcon type={s.type} />
                        <span className="text-xs font-medium capitalize">{s.type}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{s.label}</TableCell>
                    <TableCell className="text-xs">{s.tenant_name || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(s.last_sync)}</TableCell>
                    <TableCell><SyncStatusBadge status={s.status} /></TableCell>
                    <TableCell className="text-xs text-destructive max-w-[200px] truncate">{s.last_error || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </PlatformLayout>
  );
}
